"use client";

import {
  spacing,
  colors,
  fontSizes,
  radii,
} from "@softmax/design-system/tokens.stylex";

import type {
  WikiEditorState,
  WikiPermissions,
  WikiMode,
  WikiPageAddress,
  WikiLinkRenderer,
} from "../wiki-types";
import { WikiEditorFields } from "./wiki-editor-fields";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../wiki.styles";
import { SegmentedControl, Input, Button } from "@softmax/design-system";
import { CommunityMarkdown } from "@softmax/web-ux/prose";

export function WikiEditor({
  editor,
  permissions,
  mode,
  onModeChange,
  wikiPageHref,
  existingPageSlugs,
  renderLink,
  missing,
  fieldId,
}: Readonly<{
  editor: WikiEditorState;
  permissions: WikiPermissions;
  mode: WikiMode;
  onModeChange(mode: WikiMode): void;
  wikiPageHref: WikiPageAddress;
  existingPageSlugs: ReadonlySet<string>;
  renderLink: WikiLinkRenderer;
  missing: boolean;
  fieldId: string;
}>) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();

        if (!editor.pending && permissions.canEdit) editor.onSave();
      }}
      {...stylex.props(styles.form)}
    >
      <SegmentedControl
        aria-label="Editor view"
        disabled={editor.pending}
        items={[
          { value: "edit", label: "Markdown" },
          { value: "preview", label: "Preview" },
        ]}
        value={mode}
        onValueChange={onModeChange}
      />
      {mode === "preview" ? (
        <div {...stylex.props(localStyles.preview)}>
          <CommunityMarkdown
            body={editor.draft.body}
            wikiPageHref={wikiPageHref}
            existingPageSlugs={existingPageSlugs}
            demoteHeadings
            headingSlugs
            renderLink={renderLink}
          />
        </div>
      ) : (
        <WikiEditorFields editor={editor} fieldId={fieldId} />
      )}
      <label htmlFor={`${fieldId}-summary`} {...stylex.props(styles.label)}>
        <span {...stylex.props(styles.fieldTitle)}>Edit summary</span>
        <Input
          id={`${fieldId}-summary`}
          readOnly={editor.pending}
          value={editor.draft.note}
          onChange={(event) =>
            editor.onDraftChange({
              ...editor.draft,
              note: event.target.value,
            })
          }
        />
        <span {...stylex.props(localStyles.hint)}>
          One line for the revision history.
        </span>
      </label>
      <div {...stylex.props(styles.actions)}>
        <Button
          type="submit"
          variant="primary"
          disabled={editor.pending || !permissions.canEdit}
        >
          {editor.pending
            ? "Saving…"
            : missing
              ? "Create page"
              : "Save revision"}
        </Button>
        <Button onClick={editor.onCancel} disabled={editor.pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const localStyles = stylex.create({
  hint: {
    color: colors.muted,
    fontSize: fontSizes.caption,
    fontWeight: 400,
    textTransform: "none",
    letterSpacing: "normal",
  },
  preview: {
    maxWidth: "68ch",
    padding: spacing.space20,
    borderRadius: radii.medium,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
  },
});
